import type { BigIntish } from '@morpho-org/blue-sdk'

import { toBigInt } from '@morpho-org/dapp-core'
import { isDefined } from '@morpho-org/morpho-ts'

import {
  getTimelockCriticality as getTimelockCriticalityBase,
  type TimelockCriticality,
  type TimelockFunctionConfig
} from '@/components/VaultPage/helpers/timelock'

export type { TimelockCriticality, TimelockFunctionConfig }

/**
 * Vault V1 specific timelock function configurations.
 */
export const TIMELOCK_FUNCTION_CONFIG: Record<string, TimelockFunctionConfig> = {
  RemoveMarket: {
    label: 'Forced Removal',
    criticality: 'high'
  },
  SetCap: {
    label: 'Change cap',
    criticality: 'normal'
  },
  SetGuardian: {
    label: 'Change guardian',
    criticality: 'high'
  },
  SetTimelock: {
    label: 'Change timelock',
    criticality: 'high'
  }
}

/**
 * Gets the criticality level for a given timelock function name.
 * Returns "normal" as default if the function name is not found.
 */
export const getTimelockCriticality = (functionName: string): TimelockCriticality => {
  return getTimelockCriticalityBase(functionName, TIMELOCK_FUNCTION_CONFIG)
}

/**
 * Resolves the effective criticality for a V1 pending timelock item,
 * taking into account whether SetCap is an "Add Market" (no previous cap) or "Change cap".
 */
export const resolveItemCriticality = (
  item: {
    functionName: string
    decodedData?: { __typename?: string; market?: { marketId: string } | null }
  },
  marketSupplyCaps?: Record<string, BigIntish>
): TimelockCriticality => {
  if (item.functionName === 'SetCap' && marketSupplyCaps) {
    const marketId =
      item.decodedData?.__typename === 'VaultSetCapPendingData'
        ? item.decodedData.market?.marketId
        : undefined

    const hasPreviousValue = marketId ? isDefined(marketSupplyCaps[marketId]) : false

    return (
      getTimelockFunctionConfig({
        functionName: item.functionName,
        hasPreviousValue
      })?.criticality ?? 'normal'
    )
  }

  return getTimelockCriticality(item.functionName)
}

/**
 * Sorts pending timelocks by criticality (high first) then by validAt (ascending).
 * When marketSupplyCaps is provided, SetCap items without a previous cap ("Add Market")
 * are treated as high criticality.
 */
export const sortPendingTimelocks = <
  T extends {
    functionName: string
    validAt: BigIntish
    decodedData?: { __typename?: string; market?: { marketId: string } | null }
  }
>(
  items: T[],
  marketSupplyCaps?: Record<string, BigIntish>
): T[] => {
  return [...items].toSorted((a, b) => {
    const critA = resolveItemCriticality(a, marketSupplyCaps) === 'high' ? 0 : 1
    const critB = resolveItemCriticality(b, marketSupplyCaps) === 'high' ? 0 : 1

    if (critA !== critB) {
      return critA - critB
    }

    return Number(toBigInt(a.validAt) - toBigInt(b.validAt))
  })
}

/**
 * Gets the configuration (label and tooltip) for a given timelock function name.
 * Returns a default configuration if the function name is not found.
 *
 * Note: This function has V1-specific logic for handling cases without previous values.
 */
export const getTimelockFunctionConfig = ({
  functionName,
  hasPreviousValue
}: {
  functionName: string
  hasPreviousValue?: boolean
}): TimelockFunctionConfig | undefined => {
  if (!hasPreviousValue) {
    if (functionName === 'SetGuardian') {
      return {
        label: 'Add Guardian',
        criticality: 'high'
      }
    } else if (functionName === 'SetCap') {
      return {
        label: 'Add Market',
        criticality: 'high'
      }
    } else if (functionName === 'SetTimelock') {
      return {
        label: 'Add Timelock',
        criticality: 'high'
      }
    }
  }

  return TIMELOCK_FUNCTION_CONFIG[functionName]
}
